import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { App } from '@/App';
import { AuthProvider } from '@/components/auth/auth-provider';
import { UserProvider } from '@/components/auth/user-provider';
import { ThemeProvider } from '@/components/theme/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { entraEnabled, msalConfig } from '@/lib/auth-config';
import '@/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

/**
 * MSAL performs silent SSO / token renewal in hidden iframes and interactive
 * sign-in in popups. Both navigate back to this app's origin carrying the
 * auth response in the URL hash. Booting the full SPA inside those auth
 * frames is harmful: the nested MSAL instance consumes the response hash
 * before the opener/parent can read it (breaking silent SSO), and each
 * nested copy starts its own silent-SSO attempt, spawning iframes
 * recursively. Render nothing in that case.
 */
const isMsalAuthFrame =
  // Hidden iframe: in a frame AND URL carries auth-response hash.
  (window.self !== window.top &&
    /[#&?](code|state|error|error_description)=/.test(window.location.hash)) ||
  // MSAL popup: opened by another window with an "msal.*" name.
  // Requiring window.name avoids false positives on user-opened tabs that
  // have window.opener set and carry an auth-response hash after redirect.
  (!!window.opener && window.name.startsWith('msal'));

function AppTree() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <UserProvider>
                <App />
              </UserProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

if (!isMsalAuthFrame) {
  if (entraEnabled) {
    const msalInstance = new PublicClientApplication(msalConfig);

    // MSAL v4 requires initialize() to complete before any other MSAL API
    // is called, so gate the entire render on it.
    void msalInstance.initialize().then(() => {
      createRoot(document.getElementById('root')!).render(
        <StrictMode>
          <MsalProvider instance={msalInstance}>
            <AppTree />
          </MsalProvider>
        </StrictMode>
      );
    });
  } else {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <AppTree />
      </StrictMode>
    );
  }
}
